import { useState } from 'react';
import { read_cookie } from "sfcookies";
import { hostUrl, UseUrlManagerCleanUrl } from "../../../../../utils/url-manager";
import { EmailFinderResult } from "../../../../../../types/entities";

type UseEmailFinderResultsLoaderProps = {
    loadingResults: boolean,
    emailFinderResults: EmailFinderResult[],
    loadEmailFinderResults: (emailFinderId: string, searchTerm: string, page: number, pageSize: number) => void,
    totalResults: number,
    resultsError: string
}

export const useEmailFinderResultsLoader: () => UseEmailFinderResultsLoaderProps = (): UseEmailFinderResultsLoaderProps => {
    const [loadingResults, setLoadingResults] = useState<boolean>(false);
    const [emailFinderResults, setEmailFinderResults] = useState<EmailFinderResult[]>([]);
    const [totalResults, setTotalResults] = useState<number>(0);
    const [resultsError, setResultsError] = useState<string>('');

    function loadEmailFinderResults(
        emailFinderId: string,
        searchTerm: string = '',
        page: number = 1,
        pageSize: number = 10
    ): void {
        setLoadingResults(true);
        let token: string = read_cookie('auth');
        let url: string = hostUrl + 'hq/email-finder/admin/results';
        url = UseUrlManagerCleanUrl({ url: url });
        const payload = {
            'emailFinder': emailFinderId,
            'searchTerm': searchTerm,
            'page' : page,
            'pageSize' : pageSize
        };
        const request: Request = new Request(url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token
            })
        });
        fetch(request)
            .then( (response: Response): Promise<any> => {
                return response.json();
            })
            .then( (data: any): void => {
                if (data !== undefined && data['status'] === 'success') {
                    setEmailFinderResults(data.data.items);
                    setTotalResults(data.data.total);
                    // A stale error used to outlive the request that caused it, so
                    // the next scan opened showing the last one's failure.
                    setResultsError('');
                }
                if (data !== undefined && data['status'] === 'error') {
                    setResultsError(data['message']);
                }
            })
            .catch( (error: Error): void => {
                console.log(error);
                setResultsError('The results could not be loaded.');
            })
            // A network failure used to leave the grid spinning for ever.
            .finally( (): void => {
                setLoadingResults(false);
            })
    }

    return { loadingResults, emailFinderResults, loadEmailFinderResults, totalResults, resultsError }
}

type UseEmailFinderCancelProps = {
    cancelling: boolean,
    cancelEmailFinder: (finderId: string, adminId: string) => void,
    cancellingError: string,
    cancelledSuccess: boolean,
}

export const useEmailFinderCancel: () => UseEmailFinderCancelProps = (): UseEmailFinderCancelProps => {
    const [cancelling, setCanceling] = useState<boolean>(false);
    const [cancellingError, setCancellingError] = useState<string>('');
    const [cancelledSuccess, setCancelledSuccess] = useState<boolean>(false);

    function cancelEmailFinder(
        finderId: string,
        adminId: string
    ): void {
        setCanceling(true);
        setCancellingError('');
        setCancelledSuccess(false);
        let token: string = read_cookie('auth');
        let url: string = hostUrl + 'hq/email-finder/admin/cancel';
        url = UseUrlManagerCleanUrl({ url: url });
        const payload = {
            'emailFinder': finderId,
            'adminId': adminId,
        };
        const request: Request = new Request(url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token
            })
        });
        fetch(request)
            .then( (response: Response): Promise<any> => {
                return response.json();
            })
            .then( (data: any): void => {
                if (data !== undefined && data['status'] === 'success') {
                    setCancelledSuccess(true)
                }
                if (data !== undefined && data['status'] === 'error') {
                    setCancellingError(data['message']);
                }
            })
            .catch( (error: Error): void => {
                console.log(error);
                setCancellingError('The scan could not be cancelled.');
            })
            .finally( (): void => {
                setCanceling(false);
            })
    }

    return { cancelling, cancelEmailFinder, cancellingError, cancelledSuccess };
}
